import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Mail, Send, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface SendTestEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: {
    id: string;
    name: string;
    html_content: string;
    supabase_type: string;
  };
}

export const SendTestEmailModal: React.FC<SendTestEmailModalProps> = ({
  isOpen,
  onClose,
  template,
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [recipientEmail, setRecipientEmail] = useState(user?.email || '');
  const [isSending, setIsSending] = useState(false);

  const handleSendTest = async () => {
    if (!recipientEmail) {
      toast({
        title: 'E-mail obrigatório',
        description: 'Por favor, insira um e-mail de destino',
        variant: 'destructive',
      });
      return;
    }

    setIsSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-test-email', {
        body: {
          template_id: template.id,
          recipient_email: recipientEmail,
          html_content: template.html_content,
          template_name: template.name,
          supabase_type: template.supabase_type,
        },
      });

      if (error) throw error;

      toast({
        title: 'E-mail enviado!',
        description: `E-mail de teste enviado para ${recipientEmail}`,
      });
      onClose();
    } catch (error: any) {
      console.error('Error sending test email:', error);
      toast({
        title: 'Erro ao enviar',
        description: error.message || 'Não foi possível enviar o e-mail de teste',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-luxury-pinkBright" />
            Enviar E-mail de Teste
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Envie uma prévia do template "{template.name}" para testar o layout
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="recipient" className="text-white/80">
              E-mail de destino
            </Label>
            <Input
              id="recipient"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="seu@email.com"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
          </div>

          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
            <p className="text-xs text-white/50 mb-2">Dados de exemplo serão usados:</p>
            <ul className="text-xs text-white/70 space-y-1">
              <li>• <code className="text-luxury-pinkBright">{'{{ .ConfirmationURL }}'}</code> → Link de exemplo</li>
              <li>• <code className="text-luxury-pinkBright">{'{{ .Token }}'}</code> → 123456</li>
              <li>• <code className="text-luxury-pinkBright">{'{{ .SiteURL }}'}</code> → https://interlaced.app</li>
            </ul>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSendTest}
              disabled={isSending || !recipientEmail}
              className="flex-1 bg-gradient-to-r from-[#C4406B] to-[#9F3764] hover:opacity-90 text-white"
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Enviar Teste
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
